import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";

interface SiteHeaderProps {
  overlay?: boolean;
}

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = !overlay || scrolled || open;

  const linkBase =
    "relative px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap";
  const activeClass = solid ? "text-primary-600" : "text-accent-400";
  const idleClass = solid
    ? "text-foreground-700 hover:text-primary-600"
    : "text-background-50/90 hover:text-background-50";

  return (
    <header
      className={`${overlay ? "fixed" : "sticky"} left-0 right-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "border-b border-background-200/70 bg-background-50/95 backdrop-blur"
          : "border-b border-transparent bg-gradient-to-b from-black/55 to-transparent"
      }`}
    >
      <div className="hidden bg-primary-600 py-1.5 md:block">
        <div className="flex w-full items-center gap-3 overflow-hidden px-4 md:px-8">
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-background-50/20 px-2.5 py-0.5 text-xs font-medium text-background-50">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-flashlight-fill text-[10px]"></i>
            </span>
            今日更新
          </span>
          <div className="no-scrollbar flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee gap-10 whitespace-nowrap text-xs text-background-50/90">
              <span>程亦野苏念三度同框，双方工作室同一天先后回应</span>
              <span>顾言之新片定档国庆，三分钟长镜头预告刷屏</span>
              <span>陆知遥巡演收官站返场二十分钟，加唱四首</span>
              <span>程亦野苏念三度同框，双方工作室同一天先后回应</span>
              <span>顾言之新片定档国庆，三分钟长镜头预告刷屏</span>
              <span>陆知遥巡演收官站返场二十分钟，加唱四首</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-full items-center justify-between px-4 py-3 md:px-8 md:py-4">
        <Link to="/" className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-background-50">
            <i className="ri-water-flash-fill text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <strong
              className={`font-heading text-lg font-bold tracking-wide ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              {siteInfo.name}
            </strong>
            <span
              className={`mt-0.5 text-[11px] ${
                solid ? "text-foreground-600" : "text-background-50/75"
              }`}
            >
              {siteInfo.tagline}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => {
            const active =
              link.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`${linkBase} ${active ? activeClass : idleClass}`}
              >
                {link.label}
                {active && (
                  <span className="absolute -bottom-0.5 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-primary-500"></span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/daily"
            className="hidden shrink-0 items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600 md:flex"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-newspaper-line text-sm"></i>
            </span>
            看今日合集
          </Link>
          <button
            type="button"
            aria-label="打开导航菜单"
            onClick={() => setOpen((v) => !v)}
            className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors duration-200 lg:hidden ${
              solid
                ? "border-background-300/70 text-foreground-800"
                : "border-background-50/40 text-background-50"
            }`}
          >
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 transition-colors duration-200 hover:bg-primary-50 hover:text-primary-700"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <Link
            to="/daily"
            className="mt-3 flex items-center justify-center gap-1.5 rounded-full bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-newspaper-line text-sm"></i>
            </span>
            看今日合集
          </Link>
        </div>
      )}
    </header>
  );
}